import { 
  Box, Typography, Card, Stack, Button, Chip, CircularProgress,
  Alert, Input, IconButton, Tooltip, Table, Sheet, Avatar,
  FormControl, FormLabel, Select, Option, Switch, Textarea,
  Divider, Radio, RadioGroup, Checkbox, FormHelperText
} from "@mui/joy";
import {
  SaveRounded, CancelRounded, AddRounded, DeleteRounded,
  ArrowBackRounded, CategoryRounded, WarningRounded,
  DragIndicatorRounded, CheckCircleRounded, CancelRounded as CancelIcon
} from '@mui/icons-material';
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APIGetTestDetail, APICreateTest, APIUpdateTest, APIGetTestCategories, APIGetTestQuestions } from "../../api";
import { useNavigate, useParams } from "react-router";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from "./SortableItem";



export default function TestCreateEditPage() {
  const { currentDoctor, token } = useContext(LayoutContext);
  const navigate = useNavigate();
  const { testId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [test, setTest] = useState(null);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    test_type: 'psychology',
    difficulty: 'medium',
    category_ids: [],
    shuffle_questions: true,
    is_active: true,
    questions: []
  });

  const isEditMode = !!testId;
  
  // // Проверяем права пользователя
  // const isAdmin = currentDoctor?.has_type?.('admin') || currentDoctor?.type_profile?.includes('admin');
  // const isDoctor = currentDoctor?.has_type?.('doctor') || currentDoctor?.type_profile?.includes('doctor');
  
  // DnD сенсоры
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Загрузка категорий тестов
  const loadCategories = async () => {
    try {
      const response = await APIGetTestCategories(token);
      if (response.success) {
        setCategories(response.categories || []);
      }
    } catch (error) {
      console.error("Ошибка при загрузке категорий:", error);
    }
  };

  // Загрузка теста для редактирования
  const loadTest = async () => {
    if (!testId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await APIGetTestDetail(token, testId);
      
      if (response.success) {
        const testData = response.test;
        setTest(testData);
        
        // Загружаем вопросы теста
        const questionsResponse = await APIGetTestQuestions(token, testId);
        
        let questionsData = [];
        if (questionsResponse.success && questionsResponse.questions) {
            questionsData = questionsResponse.questions.map(question => ({
              id: question.id || `temp-${question.id}`,
              text: question.text || '',
              description: question.description || '',
              question_type: question.question_type || 'single_choice',
              order: question.order || 0,
              is_required: question.is_required !== undefined ? question.is_required : true,
              randomize_answers: question.randomize_answers !== undefined ? question.randomize_answers : true,
              hint_text: question.hint_text || '',
              points: question.points || 1,
              is_active: question.is_active !== undefined ? question.is_active : true,
              answers: question.answers ? question.answers.map(answer => ({
                  id: answer.id || `temp-answer-${answer.id}`,
                  text: answer.text || '',
                  description: answer.description || '',
                  is_correct: answer.is_correct || false,
                  points: answer.points || 0,
                  is_active: answer.is_active !== undefined ? answer.is_active : true
              })) : []
              }));
          }
          
          setFormData({
              name: testData.name || '',
              description: testData.description || '',
              test_type: testData.test_type || 'psychology',
              difficulty: testData.difficulty || 'medium',
              category_ids: testData.categories?.map(cat => cat.id) || [],
              shuffle_questions: testData.shuffle_questions !== undefined ? testData.shuffle_questions : true,
              is_active: testData.is_active !== undefined ? testData.is_active : true,
              questions: questionsData
          });
          
          console.log("dddd", testData.categories?.map(cat => cat.id) || [])
        } else {
          setError(testResponse.error || 'Ошибка при загрузке теста');
        }
    } catch (error) {
      console.error("Ошибка при загрузке теста:", error);
      setError("Ошибка при загрузке теста");
    } finally {
      setLoading(false);
    }
  };

  // Загрузка данных при монтировании
  useEffect(() => {
    loadCategories();
    if (isEditMode) {
      loadTest();
    }
  }, [testId]);

  // Обработчик изменений формы
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Обработчик изменений множественного выбора
  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Обработчик переключателей
  const handleSwitchChange = (name) => (event) => {
    setFormData(prev => ({
      ...prev,
      [name]: event.target.checked
    }));
  };

  // Добавление нового вопроса
  const handleAddQuestion = () => {
    const newQuestion = {
      id: `temp-${Date.now()}`,
      text: '',
      description: '',
      question_type: 'single_choice',
      order: formData.questions.length,
      is_required: true,
      randomize_answers: true,
      hint_text: '',
      points: 1,
      is_active: true,
      answers: []
    };
    
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  // Обновление вопроса
  const handleQuestionChange = (questionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((question, idx) => 
        idx === questionIndex ? { ...question, [field]: value } : question
      )
    }));
  };

  // Удаление вопроса
  const handleDeleteQuestion = (questionIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, idx) => idx !== questionIndex)
    }));
  };

  // Добавление варианта ответа
  const handleAddAnswer = (questionIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((question, idx) => {
        if (idx === questionIndex) {
          return {
            ...question,
            answers: [
              ...question.answers,
              {
                id: `temp-answer-${Date.now()}`,
                text: '',
                description: '',
                is_correct: false,
                points: 0,
                is_active: true
              }
            ]
          };
        }
        return question;
      })
    }));
  };

  // Обновление варианта ответа
  const handleAnswerChange = (questionIndex, answerIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((question, qIdx) => {
        if (qIdx === questionIndex) {
          return {
            ...question,
            answers: question.answers.map((answer, aIdx) => 
              aIdx === answerIndex ? { ...answer, [field]: value } : answer
            )
          };
        }
        return question;
      })
    }));
  };

  // Обработчик правильного ответа для одиночного выбора
  const handleCorrectAnswerChange = (questionIndex, answerIndex, isCorrect) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((question, qIdx) => {
        if (qIdx === questionIndex) {
          if (question.question_type === 'single_choice' && isCorrect) {
            // Для одиночного выбора сбрасываем другие правильные ответы
            return {
              ...question,
              answers: question.answers.map((answer, aIdx) => ({
                ...answer,
                is_correct: aIdx === answerIndex
              }))
            };
          } else {
            // Для множественного выбора просто обновляем текущий
            return {
              ...question,
              answers: question.answers.map((answer, aIdx) => 
                aIdx === answerIndex ? { ...answer, is_correct: isCorrect } : answer
              )
            };
          }
        }
        return question;
      })
    }));
  };

  // Удаление варианта ответа
  const handleDeleteAnswer = (questionIndex, answerIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((question, qIdx) => {
        if (qIdx === questionIndex) {
          return {
            ...question,
            answers: question.answers.filter((_, aIdx) => aIdx !== answerIndex)
          };
        }
        return question;
      })
    }));
  };

  // DnD обработчики
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = formData.questions.findIndex(item => item.id === active.id);
      const newIndex = formData.questions.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newQuestions = arrayMove(formData.questions, oldIndex, newIndex);
        
        // Обновляем порядок вопросов
        const updatedQuestions = newQuestions.map((question, index) => ({
          ...question,
          order: index
        }));
        
        setFormData(prev => ({
          ...prev,
          questions: updatedQuestions
        }));
      }
    }
  };

  // Валидация формы
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Название теста обязательно");
      return false;
    }

    if (formData.category_ids.length === 0) {
      setError("Выберите хотя бы одну категорию");
      return false;
    }

    // Проверяем вопросы
    for (const [index, question] of formData.questions.entries()) {
      if (!question.text.trim()) {
        setError(`Вопрос #${index + 1}: текст вопроса обязателен`);
        return false;
      }

      if (['single_choice', 'multiple_choice'].includes(question.question_type)) {
        if (question.answers.length === 0) {
          setError(`Вопрос #${index + 1}: добавьте хотя бы один вариант ответа`);
          return false;
        }

        if (question.answers.filter(a => a.is_correct).length === 0) {
          setError(`Вопрос #${index + 1}: выберите хотя бы один правильный ответ`);
          return false;
        }

        for (const [aIndex, answer] of question.answers.entries()) {
          if (!answer.text.trim()) {
            setError(`Вопрос #${index + 1}, ответ #${aIndex + 1}: текст ответа обязателен`);
            return false;
          }
        }
      }
    }

    return true;
  };

  // Сохранение теста
  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
        setSaving(true);
        setError(null);
        setSuccess(null);

        // Подготовка данных в формате JSON
        const testData = {
          name: formData.name.trim(),
          description: formData.description.trim() || '',
          test_type: formData.test_type,
          difficulty: formData.difficulty,
          categories_id: formData.category_ids,
          shuffle_questions: formData.shuffle_questions,
          is_active: formData.is_active,
        };
        
        // Добавляем вопросы если они есть
        if (formData.questions.length > 0) {
          testData.questions = formData.questions.map((question, index) => {
            const questionData = {
                    id: question.id && !String(question.id).startsWith('temp-') ? question.id : undefined,
                    text: question.text.trim(),
                    description: question.description.trim() || '',
                    question_type: question.question_type,
                    order: index,
                    is_required: question.is_required,
                    randomize_answers: question.randomize_answers,
                    hint_text: question.hint_text || '',
                    points: question.points || 1,
                    is_active: question.is_active,
                };

                // Добавляем ответы если они есть
                if (question.answers && question.answers.length > 0) {
                    questionData.answers = question.answers.map((answer) => ({
                        id: answer.id && !String(answer.id).startsWith('temp-answer-') ? answer.id : undefined,
                        text: answer.text.trim(),
                        description: answer.description.trim() || '',
                        is_correct: answer.is_correct,
                        points: answer.points || 0,
                        is_active: answer.is_active
                    }));
                }

                return questionData;
            });
        }

        // Создаем FormData и добавляем JSON как строку
        const formDataToSend = new FormData();
        formDataToSend.append('data', JSON.stringify(testData));

        let response;
        if (isEditMode) {
            response = await APIUpdateTest(token, testId, formDataToSend);
        } else {
            response = await APICreateTest(token, formDataToSend);
        }

      if (response.success) {
        setSuccess(isEditMode ? 'Тест успешно обновлен' : 'Тест успешно создан');
        
        // Перенаправляем на страницу списка тестов через 2 секунды
        setTimeout(() => {
          navigate('/tests/list');
        }, 2000);
      } else {
        setError(response.error || 'Ошибка при сохранении теста');
      }
    } catch (error) {
      console.error("Ошибка при сохранении теста:", error);
      setError("Ошибка при сохранении теста");
    } finally {
      setSaving(false);
    }
  };

  // Типы тестов
  const testTypes = [
    { value: 'psychology', label: 'Психологический' },
    { value: 'diagnostic', label: 'Диагностический' },
    { value: 'screening', label: 'Скрининговый' },
    { value: 'self_assessment', label: 'Самооценка' }
  ];

  // Уровни сложности
  const difficultyLevels = [
    { value: 'easy', label: 'Легкий' },
    { value: 'medium', label: 'Средний' },
    { value: 'hard', label: 'Сложный' }
  ];

  // Типы вопросов
  const questionTypes = [
    { value: 'single_choice', label: 'Один вариант ответа' },
    { value: 'multiple_choice', label: 'Несколько вариантов' },
    { value: 'text', label: 'Текстовый ответ' },
    { value: 'scale', label: 'Шкала (1-10)' },
    { value: 'yes_no', label: 'Да/Нет' },
    { value: 'likert', label: 'Шкала Лайкерта' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: 2,
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 2,
        }}
      >
        {/* Заголовок и кнопки */}
        <Box
          sx={{
            display: 'flex',
            mb: 1,
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'start', sm: 'center' },
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              variant="outlined"
              color="neutral"
              onClick={() => navigate('/tests/list')}
            >
              <ArrowBackRounded />
            </IconButton>
            <Typography level="h2" component="h1" startDecorator={<CategoryRounded />}>
              {isEditMode ? 'Редактирование теста' : 'Создание нового теста'}
            </Typography>
          </Stack>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<CancelRounded />}
              onClick={() => navigate('/tests/list')}
            >
              Отмена
            </Button>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<SaveRounded />}
              onClick={handleSave}
              loading={saving}
            >
              {isEditMode ? 'Сохранить' : 'Создать'}
            </Button>
          </Stack>
        </Box>

        {/* Сообщения об ошибках/успехах */}
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Основная форма */}
        <Stack spacing={3}>
          {/* Основная информация о тесте */}
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography level="h3" sx={{ mb: 0 }}>
              Основная информация
            </Typography>
            
            <Stack spacing={3}>
              <FormControl required>
                <FormLabel>Название теста</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Введите название теста"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Описание теста</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Введите описание теста"
                  minRows={3}
                />
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <FormControl required sx={{ flex: 1 }}>
                  <FormLabel>Тип теста</FormLabel>
                  <Select
                    name="test_type"
                    value={formData.test_type}
                    onChange={(e, value) => handleSelectChange('test_type', value)}
                  >
                    {testTypes.map((type) => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl required sx={{ flex: 1 }}>
                  <FormLabel>Уровень сложности</FormLabel>
                  <Select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={(e, value) => handleSelectChange('difficulty', value)}
                  >
                    {difficultyLevels.map((level) => (
                      <Option key={level.value} value={level.value}>
                        {level.label}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <FormControl required>
                <FormLabel>Категории теста</FormLabel>

                <Select
                  multiple
                  name="category_ids"
                  value={formData.category_ids}
                  onChange={(e, value) => handleSelectChange('category_ids', value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((item) => {
                        const category = categories.find(cat => cat.id === item.value);
                        return category ? (
                          <Chip key={item.value} size="sm" variant="soft" color="primary">
                            {category.name}
                          </Chip>
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {categories.map((category) => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <Stack spacing={2}>
                <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <FormLabel>Перемешивать вопросы</FormLabel>
                    <FormHelperText>
                      Вопросы будут показываться в случайном порядке
                    </FormHelperText>
                  </Box>
                  <Switch
                    checked={formData.shuffle_questions}
                    onChange={handleSwitchChange('shuffle_questions')}
                    color={formData.shuffle_questions ? 'primary' : 'neutral'}
                  />
                </FormControl>

                <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <FormLabel>Активный тест</FormLabel>
                    <FormHelperText>
                      {formData.is_active ? 'Тест доступен для прохождения' : 'Тест скрыт от пользователей'}
                    </FormHelperText>
                  </Box>
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange('is_active')}
                    color={formData.is_active ? 'success' : 'neutral'}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </Card>

          {/* Вопросы теста */}
          <Card variant="outlined">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography level="h3">
                Вопросы теста{' '}
                <Typography level="body2" color="neutral" sx={{ mt: 0.5 }}>
                  {formData.questions.length} вопросов
                </Typography>
              </Typography>
              <Button
                startDecorator={<AddRounded />}
                onClick={handleAddQuestion}
              >
                Добавить вопрос
              </Button>
            </Box>

            {formData.questions.length === 0 ? (
              <Alert color="neutral">
                Добавьте вопросы к тесту
              </Alert>
            ) : (
                <Stack spacing={3}>
                  {formData.questions.map((question, qIndex) => (
                    <QuestionItem
                      question={question}
                      index={qIndex}
                      questionTypes={questionTypes}
                      onChange={handleQuestionChange}
                      onDelete={handleDeleteQuestion}
                      onAddAnswer={handleAddAnswer}
                      onAnswerChange={handleAnswerChange}
                      onCorrectAnswerChange={handleCorrectAnswerChange}
                      onDeleteAnswer={handleDeleteAnswer}
                    />
                  ))}
                </Stack>
            )}
            <Button
              startDecorator={<AddRounded />}
              onClick={handleAddQuestion}
            >
              Добавить вопрос
            </Button>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
}

// Компонент вопроса
function QuestionItem({ question, index, questionTypes, onChange, onDelete, onAddAnswer, onAnswerChange, onCorrectAnswerChange, onDeleteAnswer }) {
  const questionNumber = index + 1;

  return (
    <Card variant="soft" color="primary" sx={{ position: 'relative' }}>
      {/* Заголовок вопроса */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography level="title-lg">
          Вопрос #{questionNumber}
        </Typography>
        <IconButton
          color="danger"
          variant="soft"
          size="sm"
          onClick={() => onDelete(index)}
        >
          <DeleteRounded />
        </IconButton>
      </Box>

      {/* Форма вопроса */}
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <FormControl required sx={{ flex: 1 }}>
            <FormLabel>Тип вопроса</FormLabel>
            <Select
              value={question.question_type}
              onChange={(e, value) => onChange(index, 'question_type', value)}
            >
              {questionTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
            <FormLabel>Баллы</FormLabel>
            <Input
              type="number"
              value={question.points}
              onChange={(e) => onChange(index, 'points', parseInt(e.target.value) || 0)}
            />
          </FormControl>
        </Stack>

        <FormControl required>
          <FormLabel>Текст вопроса</FormLabel>
          <Textarea
            value={question.text}
            onChange={(e) => onChange(index, 'text', e.target.value)}
            placeholder="Введите текст вопроса"
            minRows={2}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Описание вопроса (необязательно)</FormLabel>
          <Textarea
            value={question.description || ''}
            onChange={(e) => onChange(index, 'description', e.target.value)}
            placeholder="Введите описание вопроса"
            minRows={2}
          />
        </FormControl>

        {question.question_type === 'scale' && (
          <Alert color="info" variant="soft">
            Для шкалы (1-10) система автоматически создаст варианты от 1 до 10
          </Alert>
        )}

        {question.question_type === 'yes_no' && (
          <Alert color="info" variant="soft">
            Для типа Да/Нет система автоматически создаст варианты "Да" и "Нет"
          </Alert>
        )}

        {question.question_type === 'likert' && (
          <Alert color="info" variant="soft">
            Для шкалы Лайкерта система автоматически создаст варианты от "Полностью не согласен" до "Полностью согласен"
          </Alert>
        )}

        {/* Варианты ответов (только для single_choice и multiple_choice) */}
        {['single_choice', 'multiple_choice'].includes(question.question_type) && (
          <Box>
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography level="title-md">
                Варианты ответов
              </Typography>
              <Button
                size="sm"
                startDecorator={<AddRounded />}
                onClick={() => onAddAnswer(index)}
              >
                Добавить вариант
              </Button>
            </Box>

            {question.answers.length === 0 ? (
              <Alert color="neutral">
                Добавьте варианты ответов
              </Alert>
            ) : (
              <Stack spacing={2}>
                {question.answers.map((answer, aIndex) => (
                  <AnswerItem
                    key={aIndex}
                    answer={answer}
                    questionType={question.question_type}
                    index={aIndex}
                    onAnswerChange={(field, value) => onAnswerChange(index, aIndex, field, value)}
                    onCorrectChange={(isCorrect) => onCorrectAnswerChange(index, aIndex, isCorrect)}
                    onDelete={() => onDeleteAnswer(index, aIndex)}
                  />
                ))}
              </Stack>
            )}
            <Button
              size="sm"
              startDecorator={<AddRounded />}
              onClick={() => onAddAnswer(index)}
              sx={{ mt: 2, width: '100%' }}
            >
              Добавить вариант
            </Button>
          </Box>
        )}

        {/* Настройки вопроса */}
        <Divider sx={{ my: 2 }} />
        
        <Stack spacing={2}>
          <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <Box>
              <FormLabel>Обязательный вопрос</FormLabel>
              <FormHelperText>
                {question.is_required ? 'Пользователь должен ответить на вопрос' : 'Вопрос можно пропустить'}
              </FormHelperText>
            </Box>
            <Switch
              checked={question.is_required}
              onChange={(e) => onChange(index, 'is_required', e.target.checked)}
              color={question.is_required ? 'primary' : 'neutral'}
            />
          </FormControl>

          {['single_choice', 'multiple_choice'].includes(question.question_type) && (
            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Перемешивать варианты ответов</FormLabel>
                <FormHelperText>
                  Варианты ответов будут показываться в случайном порядке
                </FormHelperText>
              </Box>
              <Switch
                checked={question.randomize_answers}
                onChange={(e) => onChange(index, 'randomize_answers', e.target.checked)}
                color={question.randomize_answers ? 'primary' : 'neutral'}
              />
            </FormControl>
          )}

          <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <Box>
              <FormLabel>Активный вопрос</FormLabel>
              <FormHelperText>
                {question.is_active ? 'Вопрос будет показан в тесте' : 'Вопрос будет скрыт'}
              </FormHelperText>
            </Box>
            <Switch
              checked={question.is_active}
              onChange={(e) => onChange(index, 'is_active', e.target.checked)}
              color={question.is_active ? 'success' : 'neutral'}
            />
          </FormControl>
        </Stack>
      </Stack>
    </Card>
  );
}

// Компонент варианта ответа
function AnswerItem({ answer, questionType, index, onAnswerChange, onCorrectChange, onDelete }) {
  const answerNumber = index + 1;

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography level="title-sm">Вариант #{answerNumber}</Typography>
          <IconButton
            color="danger"
            variant="soft"
            size="sm"
            onClick={onDelete}
          >
            <DeleteRounded />
          </IconButton>
        </Box>

        <FormControl required>
          <FormLabel>Текст ответа</FormLabel>
          <Input
            value={answer.text}
            onChange={(e) => onAnswerChange('text', e.target.value)}
            placeholder="Введите текст ответа"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Описание ответа (необязательно)</FormLabel>
          <Textarea
            value={answer.description || ''}
            onChange={(e) => onAnswerChange('description', e.target.value)}
            placeholder="Введите описание ответа"
            minRows={2}
          />
        </FormControl>

        <Stack direction="row" spacing={3} alignItems="center">
          <FormControl sx={{ minWidth: 120 }}>
            <FormLabel>Баллы за ответ</FormLabel>
            <Input
              type="number"
              value={answer.points || 0}
              onChange={(e) => onAnswerChange('points', parseInt(e.target.value) || 0)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Правильный ответ</FormLabel>
            {questionType === 'single_choice' ? (
              <Radio
                checked={answer.is_correct}
                onChange={(e) => onCorrectChange(e.target.checked)}
                color="success"
                label={answer.is_correct ? "Правильный" : "Отметить как правильный"}
              />
            ) : (
              <Checkbox
                checked={answer.is_correct}
                onChange={(e) => onCorrectChange(e.target.checked)}
                color="success"
                label="Правильный ответ"
              />
            )}
          </FormControl>

          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              size="sm"
              variant="soft"
              color={answer.is_active ? 'success' : 'neutral'}
              startDecorator={answer.is_active ? <CheckCircleRounded /> : <CancelIcon />}
            >
              {answer.is_active ? 'Активен' : 'Неактивен'}
            </Chip>
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}